import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { formatDuration } from './taskConstants';

/** Mirrors the server ceiling in tasks.routes.ts — reject before uploading, not after. */
const MAX_VOICE_BYTES = 25 * 1024 * 1024;

/**
 * Safari/iOS does not support webm at all, so the type has to be negotiated rather
 * than assumed. An empty string is a valid last resort — it lets the browser pick.
 */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

export interface VoiceNoteDraft {
  blob: Blob;
  durationMs: number;
}

interface VoiceNoteRecorderProps {
  value: VoiceNoteDraft | null;
  onChange: (draft: VoiceNoteDraft | null) => void;
  /** Rendered when there is no draft — lets Edit show the note already on the task. */
  existing?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Records one optional voice note. Deliberately holds no upload logic: the blob is
 * kept here and only leaves when the parent form is submitted, so abandoning the
 * form uploads nothing and leaves no orphaned file on the server.
 */
export function VoiceNoteRecorder({ value, onChange, existing, disabled }: VoiceNoteRecorderProps) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Releases the mic so the browser's recording indicator actually goes out. */
  const releaseStream = useCallback(() => {
    recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Derived rather than stored, so there is no setState-in-effect cascade; the
  // effect below exists only to revoke the previous URL when it changes or unmounts.
  const previewUrl = useMemo(() => (value ? URL.createObjectURL(value.blob) : null), [value]);
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => releaseStream, [releaseStream]);

  async function start() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('This browser cannot record audio');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Denial and "no microphone" are indistinguishable enough in practice, and the
      // fix is the same either way. getUserMedia also needs a secure context, so this
      // is what a plain-HTTP LAN visit hits.
      toast.error('Microphone unavailable — allow mic access and use HTTPS or localhost');
      return;
    }

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const durationMs = Date.now() - startedAtRef.current;
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      chunksRef.current = [];
      releaseStream();
      setRecording(false);
      setElapsed(0);
      if (blob.size === 0) {
        toast.error('Nothing was recorded');
        return;
      }
      if (blob.size > MAX_VOICE_BYTES) {
        toast.error('Recording exceeds the 25MB limit — record a shorter note');
        return;
      }
      onChange({ blob, durationMs });
    };

    startedAtRef.current = Date.now();
    recorder.start();
    setRecording(true);
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed(Date.now() - startedAtRef.current), 250);
  }

  function stop() {
    recorderRef.current?.stop();
  }

  const label = 'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

  return (
    <div className="space-y-1.5">
      <p className={label}>Voice Note <span className="normal-case font-normal">(optional)</span></p>

      {recording ? (
        <div className="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 dark:border-rose-900/40 dark:bg-rose-900/20">
          <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-rose-500" />
          <span className="text-sm font-medium text-rose-700 tabular-nums dark:text-rose-400">
            Recording {formatDuration(elapsed)}
          </span>
          <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={stop}>
            <Square className="h-4 w-4" /> Stop
          </Button>
        </div>
      ) : value && previewUrl ? (
        <div className="space-y-2 rounded-lg border border-border/60 bg-card p-3">
          <audio controls src={previewUrl} className="w-full" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatDuration(value.durationMs)}
            </span>
            <Button
              type="button" size="sm" variant="outline" className="ml-auto"
              onClick={start} disabled={disabled}
            >
              <Mic className="h-4 w-4" /> Re-record
            </Button>
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => onChange(null)} disabled={disabled}
            >
              <Trash2 className="h-4 w-4 text-destructive" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {existing}
          <Button type="button" size="sm" variant="outline" onClick={start} disabled={disabled}>
            <Mic className="h-4 w-4" /> Record Voice Note
          </Button>
        </div>
      )}
    </div>
  );
}
